import { memo, useState } from 'react';
import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import StepContent from '@mui/material/StepContent';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import { useSettings, useSimpleRequest, useStepper } from 'hooks';
import { delay, when } from 'util/support';
import { WebRoutes } from 'consts/enpoints/web';
import { ECommerceShoppingPlatform } from 'consts/ecommerce';
import { useNavigate, useParams } from 'infrastructure/router';
import { putOnboardingsBusinessInformationSteps } from 'features/ecommerce/api';
import type { MerchantSettings } from 'features/merchants/types';
import ShoppingPlatforms from 'features/ecommerce/ShoppingPlatforms';
import useUser from 'features/auth/useUser';
import { DownloadPluginContent, DownloadPluginLabel } from './DownloadPlugin';
import { SetupPluginContent, SetupPluginLabel } from './SetupPlugin';

const steps = [
    {
        Label: DownloadPluginLabel,
        Content: DownloadPluginContent
    },
    {
        Label: SetupPluginLabel,
        Content: SetupPluginContent
    }
];

const PlatformSetupStepper = () => {
    const [isLoading, setLoading] = useState(false);

    const { platform } = useParams<{
        readonly platform: ECommerceShoppingPlatform
    }>();

    const navigate = useNavigate();

    const request = useSimpleRequest({ setLoading });

    const {
        step,
        next,
        back
    } = useStepper();

    const { user } = useUser();

    const { getSettings } = useSettings<MerchantSettings>();

    const isLastStep = (step: number) => Object.is(step, steps.length - 1);

    const { platforms } = getSettings();

    const handleNext = () => when(
        isLastStep(step),
        () => when(
            platforms,
            () => request(() => putOnboardingsBusinessInformationSteps({
                userId: user.pki,
                platforms: ShoppingPlatforms.make(getSettings().platforms)
                    .parse()
                    .activate(platform!)
                    .serialize()
            }), {
                onSuccess: () => (
                    delay(500)
                        .then(() => {
                            window.location.href = WebRoutes.PaymentMethods;
                        })
                )
            }),
            () => navigate(WebRoutes.PaymentMethods)
        ),
        next
    );

    return (
        <Box
            sx={{
                maxWidth: 400
            }}
        >
            <Stepper
                activeStep={step}
                orientation='vertical'
            >
                {steps.map((step, index) => (
                    <Step
                        key={index}
                    >
                        <StepLabel>
                            <step.Label />
                        </StepLabel>
                        <StepContent>
                            <step.Content />
                            <Box
                                sx={{
                                    mb: 2
                                }}
                            >
                                <LoadingButton
                                    sx={{
                                        mt: 2,
                                        mr: 1
                                    }}
                                    loading={isLoading}
                                    variant='contained'
                                    onClick={handleNext}
                                >
                                    {when(
                                        isLastStep(index),
                                        'Proceed to Payment Methods',
                                        'Move to next step'
                                    )}
                                </LoadingButton>
                                <Button
                                    sx={{
                                        mt: 2,
                                        mr: 1
                                    }}
                                    disabled={!index}
                                    onClick={back}
                                >
                                    Back
                                </Button>
                            </Box>
                        </StepContent>
                    </Step>
                ))}
            </Stepper>
        </Box>
    );
};

export default memo(PlatformSetupStepper);
